import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
];

const POLL_INTERVAL = 10000; // 10 seconds

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
};

const formatElapsed = (seconds) => {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const getStatusBadge = (status) => {
  const map = {
    pending: { cls: 'admin-queue__badge--pending', label: 'Queued' },
    completed: { cls: 'admin-queue__badge--completed', label: 'Completed' },
    failed: { cls: 'admin-queue__badge--failed', label: 'Failed' },
  };
  if (status?.includes('in_progress')) {
    return { cls: 'admin-queue__badge--running', label: 'Running' };
  }
  return map[status] || map.pending;
};

const AdminQueuePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchQueue = useCallback(async (tab) => {
    try {
      const response = await fetch(`/api/meta_ads_ai/reports/admin_queue?tab=${tab}`, {
        headers: { 'Accept': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (err) {
      console.error('[AdminQueuePage] Error fetching queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchQueue(activeTab);

    // Poll on running tab
    if (activeTab === 'running' || activeTab === 'all') {
      pollRef.current = setInterval(() => fetchQueue(activeTab), POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [activeTab, fetchQueue]);

  const handleRowClick = (item) => {
    if (item.status === 'completed' && item.facebook_campaign_id) {
      navigate(`/marketing/meta_ads_ai/companies/${item.company_slug}/campaigns/${item.facebook_campaign_id}`);
    } else if (item.company_slug) {
      navigate(`/marketing/meta_ads_ai/companies/${item.company_slug}`);
    }
  };

  return (
    <div className="admin-queue">
      <div className="admin-queue__header">
        <h2 className="admin-queue__title">AI Generation Queue</h2>
        <p className="admin-queue__subtitle">All companies</p>
      </div>

      <div className="admin-queue__tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={`admin-queue__tab ${activeTab === tab.key ? 'admin-queue__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-queue__loading">Loading...</div>
      ) : items.length === 0 ? (
        <div className="admin-queue__empty">
          <p>No reports found for this filter.</p>
        </div>
      ) : (
        <div className="admin-queue__table-wrap">
          <table className="admin-queue__table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Event</th>
                <th>Event Dates</th>
                <th>Started</th>
                <th>Status</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const badge = getStatusBadge(item.status);
                return (
                  <tr
                    key={item.id}
                    className="admin-queue__row"
                    onClick={() => handleRowClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && handleRowClick(item)}
                  >
                    <td className="admin-queue__company">{item.company_name || '—'}</td>
                    <td>{item.event_name || '—'}</td>
                    <td>
                      {formatDate(item.event_start_date)}
                      {item.event_end_date && ` – ${formatDate(item.event_end_date)}`}
                    </td>
                    <td>
                      <div>{formatDate(item.created_at)}</div>
                      {item.status?.includes('in_progress') && (
                        <div className="admin-queue__elapsed">{formatElapsed(item.elapsed_seconds)}</div>
                      )}
                    </td>
                    <td>
                      <span className={`admin-queue__badge ${badge.cls}`}>
                        {item.status?.includes('in_progress') && (
                          <span className="admin-queue__spinner" />
                        )}
                        {badge.label}
                      </span>
                      {item.stage_label && item.status?.includes('in_progress') && (
                        <div className="admin-queue__stage">{item.stage_label}</div>
                      )}
                    </td>
                    <td>{item.estimated_cost || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminQueuePage;
